import styled, { css } from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../../../components/AppShell/styles";

const MOBILE = `not all and (min-width: ${DESKTOP_BREAKPOINT})`;

/** A link in the list, as a Keepo's card in Meus Keepos: opened with a click anywhere on it. */
export const Card = styled.article`
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 1rem;
  cursor: pointer;
  background-color: var(--color-neutral-dark);
  border: 1px solid transparent;
  border-radius: 1rem;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--color-neutral-base);
  }

  &[aria-busy="true"] {
    opacity: 0.6;
  }

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    gap: 1.25rem;
    padding: 1rem 1.25rem 1rem 1rem;
  }
`;

/** The site's icon (a globe without one), where a Keepo's card has its thumbnail. */
export const Favicon = styled.span`
  display: grid;
  flex-shrink: 0;
  place-content: center;
  width: 40px;
  height: 40px;
  overflow: hidden;
  color: var(--color-neutral-150);
  background: var(--color-neutral-darkness);
  border-radius: 12px;

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }

  svg {
    width: 22px;
    height: 22px;
  }

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    width: 56px;
    height: 56px;
  }
`;

export const Info = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;

  /* the short link · where it goes, on one line ending in "…" */
  .url {
    overflow: hidden;
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
    text-overflow: ellipsis;
    white-space: nowrap;

    .short {
      font-weight: 700;
      color: var(--color-neutral-white);
    }
  }

  [role="alert"] {
    font-size: 0.75rem;
    color: #ff8a80;
  }
`;

export const TitleRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  min-width: 0;

  /* Always a single line: long titles end with an ellipsis */
  h4 {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    a {
      color: var(--color-neutral-white);
    }
  }
`;

/** Whether the link is on, with a dot, as a Keepo's "Ativo" / "Inativo"; the rest of its line after it. */
export const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  align-items: center;
  margin-top: 0.25rem;
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-neutral-150);
`;

export const Status = styled.span`
  display: inline-flex;
  gap: 0.375rem;
  align-items: center;
  font-weight: 700;

  &.active {
    color: var(--color-feedback-success);
  }

  &.unsafe {
    color: var(--color-feedback-danger);
  }

  &::before {
    width: 6px;
    height: 6px;
    content: "";
    background-color: currentColor;
    border-radius: 100%;
  }
`;

/** The link's tags, on their own line under the status one, wrapping when there are many. */
export const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.25rem;
`;

export const Tag = styled.button`
  padding: 0 8px;
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1.25rem;
  color: var(--color-neutral-white);
  background: var(--color-neutral-base);
  border-radius: 999px;

  &::before {
    content: "#";
    opacity: 0.6;
  }
`;

/** "Pausado" / "Bloqueado" beside the title of the link's screen. */
export const Badge = styled.span`
  flex-shrink: 0;
  padding: 2px 8px;
  font-family: Inter, sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1rem;
  color: var(--color-neutral-white);
  text-transform: uppercase;
  background: var(--color-neutral-base);
  border-radius: 6px;

  &.danger {
    background: #b42318;
  }
`;

/** Copy, "⋯" and the chevron, at the end of the card (as a Keepo's open and chevron). */
export const Actions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 0.5rem;
  align-items: center;
  cursor: default;

  .chevron {
    display: flex;
    margin-left: 0.25rem;
    color: var(--color-neutral-150);
  }

  /* On a phone, copy lives on the link's screen (as a Keepo's "open", a desktop-only shortcut) */
  @media ${MOBILE} {
    gap: 0;

    .copy,
    .chevron {
      display: none;
    }
  }
`;

/** A round button as a Keepo's "open"; "✓ Copiado" for a moment after copying. */
export const CopyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-neutral-white);
  white-space: nowrap;
  background: var(--color-neutral-base);
  border-radius: 999px;

  /* On the link's screen: the icon and "Copiar", as a pill */
  &.pill {
    flex-shrink: 0;
    gap: 0.375rem;
    height: 36px;
    padding: 0 14px;
  }
`;

/**
 * The card of choices opened under a button (a link's "⋯", the period): shared, so they all look alike.
 * `button`s inside, `.danger` in red, `[aria-checked="true"]` in bold (its check drawn by the menu).
 */
export const menuItemsCss = css`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  min-width: 160px;
  padding: 6px;
  background: var(--color-neutral-darkness);
  border: 1px solid var(--color-neutral-base);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);

  button {
    padding: 8px 12px;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    color: var(--color-neutral-white);
    text-align: left;
    white-space: nowrap;
    border-radius: 8px;

    &:hover {
      background: var(--color-neutral-dark);
    }

    &.danger {
      color: #ff8a80;
    }

    &[aria-checked="true"] {
      display: flex;
      gap: 1rem;
      align-items: center;
      justify-content: space-between;
      font-weight: 700;
    }
  }
`;

/** The "⋯" of a link (round, as copy beside it) and its actions, opened under it. */
export const Menu = styled.div`
  position: relative;
  flex-shrink: 0;

  .more {
    display: grid;
    place-content: center;
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
    color: var(--color-neutral-white);
    background: var(--color-neutral-base);
    border-radius: 100%;
  }

  .items {
    ${menuItemsCss};
  }
`;
